import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './WidgetApp.css';
import { AnimatedPrice } from './components/simulator/Summary';
import { API, formatEuro, validateField } from './components/simulator/model';
import { useEmbedding } from './components/simulator/useEmbedding';
import { useFunnel } from './components/simulator/useFunnel';
import { ContactStep, PrivacyNotice } from './components/simulator/ContactStep';
import { PrepareVisit } from './components/simulator/Confirmation';
import { Icon } from './components/simulator/Icon';
import { useSlots } from './components/booking/useSlots';
import { SlotSelector, BookingError } from './components/booking/SlotSelector';
import { BookingConfirmation } from './components/booking/BookingConfirmation';
import { Consent } from './components/booking/Consent';
import { CancelDialog } from './components/booking/CancelDialog';
import { ClientAddress } from './components/booking/ClientAddress';
import { bookVisit, errorDetails, rememberAccess, slotLabel } from './components/booking/model';

const getAccess = () => {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const supplied = hash.get('access') || window.ebPersonalAccess;
  let token = supplied;
  if (supplied) window.ebPersonalAccess = supplied;
  try { if (supplied) sessionStorage.setItem('eb_client_page_access', supplied); else token = sessionStorage.getItem('eb_client_page_access'); } catch (_) { /* Le lien garde son accès en mémoire. */ }
  if (supplied) window.history.replaceState(null, '', window.location.pathname);
  return token;
};
const fieldAliases = { nom: 'last_name', prenom: 'first_name', telephone: 'phone', adresse: 'address' };

export default function ClientQuotePage() {
  const [token, setToken] = useState(getAccess);
  const [quote, setQuote] = useState(null);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [addressEditing, setAddressEditing] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [callback, setCallback] = useState(false);
  const [session, setSession] = useState(() => crypto.randomUUID());
  const root = useRef(null);
  const working = useRef(false);
  const activeAccess = useRef(token);
  activeAccess.current = token;
  const loadController = useRef(null);
  const { dock, editing: typing } = useEmbedding(root);
  const track = useFunnel(session);
  const slots = useSlots({ prestation: quote?.prestation, adresse: quote?.address, devisId: quote?.id, token, enabled: editing && !callback && Boolean(quote?.prestation) });
  const load = useCallback(async () => {
    loadController.current?.abort();
    const controller = new AbortController();
    loadController.current = controller;
    if (!token) { setLoading(false); setError({ code: 'ACCES_REQUIS', message: 'Ouvrez votre lien personnel depuis l’e-mail du devis, ou appelez-nous au 06 18 15 98 73.' }); return; }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/client/quote`, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal });
      if (controller.signal.aborted) return;
      const item = rememberAccess({ ...response.data, client_token: token });
      setQuote(item); setData(item);
      if (item.funnel_session_id) setSession(item.funnel_session_id);
      setError(item.booking_uncertain ? { code: 'RESULTAT_INCONNU', uncertain: true, message: 'La confirmation de votre demande reste à vérifier. Appelez-nous avant de réserver à nouveau pour éviter un doublon.' } : null);
      if (item.booking) setEditing(false);
    } catch (err) { if (!controller.signal.aborted) setError(errorDetails(err)); }
    finally { if (!controller.signal.aborted) setLoading(false); }
  }, [token]);
  useEffect(() => { load(); return () => loadController.current?.abort(); }, [load]);
  useEffect(() => {
    const changed = () => {
      if (!new URLSearchParams(window.location.hash.slice(1)).get('access')) return;
      const nextToken = getAccess();
      if (nextToken === token) return;
      loadController.current?.abort();
      setToken(nextToken); setQuote(null); setData({}); setError(null); setErrors({});
      setEditing(false); setAddressEditing(false); setConsent(false); setCallback(false); setCancelOpen(false);
    };
    window.addEventListener('hashchange', changed);
    return () => window.removeEventListener('hashchange', changed);
  }, [token]);
  const update = (patch) => { setData((previous) => ({ ...previous, ...patch })); setErrors((previous) => Object.fromEntries(Object.entries(previous).filter(([key]) => !(key in patch)))); };
  const onBlur = (name) => setErrors((previous) => ({ ...previous, [name]: validateField(name, data) }));
  const formValid = ['civilite', 'last_name', 'phone', 'email'].every((key) => !validateField(key, data));
  const canBook = !busy && formValid && slots.slot && consent && !slots.loading && !slots.error && !error?.uncertain;
  const startEditing = () => { setEditing(true); setConsent(false); setCallback(false); setError(null); slots.reload(); };
  const finishBooking = async () => {
    if (!canBook || working.current) return;
    working.current = true; setBusy(true); setError(null);
    const modifying = quote.rdv_status === 'RDV confirmé';
    try {
      const booking = await bookVisit(quote, data, slots.slot, consent);
      if (activeAccess.current !== token) return;
      setQuote((previous) => ({ ...previous, booking, rdv_status: 'RDV confirmé' })); setEditing(false); setConsent(false);
      track(modifying ? 'booking_modified' : 'booking_confirmed', 'confirmation');
      root.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
    } catch (err) {
      if (activeAccess.current !== token) return;
      const details = errorDetails(err);
      if (details.uncertain) details.message = 'La demande a été transmise, mais sa confirmation reste à vérifier. Appelez-nous avant de réserver à nouveau pour éviter un doublon.';
      setError(details); setErrors(Object.fromEntries(Object.entries(details.fields || {}).map(([key, value]) => [fieldAliases[key] || key, value])));
      track('booking_error', 'visite', { code: details.code });
      if (details.code === 'CRENEAU_PRIS') slots.reload();
      if (details.code === 'ADRESSE_INTROUVABLE' && !quote.booking) { setEditing(false); setAddressEditing(true); }
    } finally { working.current = false; setBusy(false); }
  };
  const cancel = async () => {
    if (working.current) return;
    working.current = true; setBusy(true);
    try {
      await axios.post(`${API}/api/annuler`, { devis_id: quote.id }, { headers: { Authorization: `Bearer ${token}` }, timeout: 35000 });
      if (activeAccess.current !== token) return;
      setQuote((previous) => ({ ...previous, booking: null, rdv_status: 'RDV annulé' }));
      track('booking_cancelled', 'confirmation'); setError(null);
    } catch (err) { const details = errorDetails(err); setError(details); track('booking_error', 'confirmation', { code: details.code }); }
    finally { setCancelOpen(false); working.current = false; setBusy(false); }
  };
  const preferCallback = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/client/quote/callback`, { devis_id: quote.id }, { headers: { Authorization: `Bearer ${token}` } });
      if (activeAccess.current !== token) return;
      setCallback(true); setEditing(false); track('callback_requested', 'confirmation');
    } catch (err) { setError(errorDetails(err)); }
    finally { setBusy(false); }
  };
  const label = busy ? 'Confirmation en cours…' : `Confirmer ma visite${slots.slot ? ` · ${slotLabel(slots.slot)}` : ''}`;
  const primary = (mobile) => <button type="button" className="sim-primary" data-testid={mobile ? 'client-mobile-confirm' : 'client-book-confirm'} disabled={!canBook} onClick={finishBooking}>{label}</button>;

  return <div className={`sim-app booking-client ${!editing ? 'sim-complete' : ''}`} ref={root}>
    <main className="booking-client-main">
      <header className="booking-client-brand"><a href="/" data-testid="client-home">EB ENERGIE</a></header>
      {loading ? <p role="status" data-testid="client-loading">Chargement de votre devis…</p> : <>
        <BookingError error={error} retry={load} onVerify={load} onAddress={quote && !quote.booking ? () => { setEditing(false); setAddressEditing(true); } : undefined} />
        {quote && <>
          {callback && quote.booking && <p className="booking-fallback" data-testid="client-callback-with-booking">Votre demande de rappel est enregistrée. Votre rendez-vous actuel est conservé.</p>}
          {addressEditing ? <ClientAddress quote={quote} token={token} track={track} onCancel={() => { setAddressEditing(false); setEditing(true); }} onSaved={(address) => { setQuote((previous) => ({ ...previous, address })); update({ address }); setAddressEditing(false); setEditing(true); setError(null); slots.reload(); }} /> : editing ? <section className="booking-client-card" data-testid="client-booking-form">
            <button type="button" className="sim-back" data-testid="client-booking-back" disabled={busy} onClick={() => setEditing(false)}><Icon name="back" size={16} /> Retour</button>
            <h1 id="sim-step-title" data-testid="client-booking-title">{quote.booking ? 'Modifiez votre visite' : 'Choisissez votre visite'}</h1>
            <SlotSelector slots={slots} track={track} disabled={busy} onAddress={!quote.booking ? () => { setEditing(false); setAddressEditing(true); } : undefined} />
            {!slots.fallback && <><ContactStep data={data} update={update} errors={errors} onBlur={onBlur} hidePreference /><Consent checked={consent} onChange={setConsent} error={errors.consentement_execution} disabled={busy} /><div className="sim-actions"><span className="sim-actions-total" data-testid="client-inline-total"><span>Total TTC</span><AnimatedPrice total={quote.total_ttc} ready={true} testid="client-inline-total-amount" /></span>{primary(false)}</div><PrivacyNotice /></>}
            <button type="button" className="sim-text-button" data-testid="client-prefer-callback" disabled={busy} onClick={preferCallback}>Je préfère être rappelé</button>
          </section> : quote.booking || quote.rdv_status === 'RDV annulé' ? <BookingConfirmation booking={quote.booking} quote={quote} data={data} cancelled={quote.rdv_status === 'RDV annulé'} onModify={startEditing} onCancel={() => setCancelOpen(true)} /> : <section className="sim-confirmation" data-testid="client-quote-overview">
            <p className="sim-eyebrow">DEVIS {quote.id}</p><h1 data-testid="client-quote-title">Votre devis</h1>
            <p className="sim-lede" data-testid="client-quote-name">{data.civilite} {data.last_name?.toUpperCase()}</p>
            <dl className="booking-details"><div><dt>Adresse</dt><dd data-testid="client-quote-address">{quote.address}</dd></div><div><dt>Prix TTC</dt><dd className="booking-price" data-testid="client-quote-price">{formatEuro(quote.total_ttc)}</dd></div></dl>
            {callback || !quote.prestation ? <p className="booking-fallback" data-testid="client-callback-confirmed">Nous vous appelons pour fixer la visite.</p> : <button type="button" className="sim-primary" data-testid="client-confirm-appointment" disabled={error?.uncertain} onClick={startEditing}>Confirmer le rendez-vous <Icon name="arrow" size={18} /></button>}
            <div><a className="sim-text-button" href={quote.pdf_url} data-testid="client-download-pdf" target="_blank" rel="noopener noreferrer">Télécharger mon devis PDF</a></div>
            <PrepareVisit /><a href="tel:+33618159873" className="sim-call" data-testid="client-quote-call"><Icon name="phone" size={18} /> 06 18 15 98 73</a>
          </section>}
          <CancelDialog open={cancelOpen} busy={busy} onClose={() => setCancelOpen(false)} onConfirm={cancel} />
        </>}
      </>}
    </main>
    {editing && quote && !slots.fallback && <div className={`sim-mobile-bar booking-active ${typing ? 'sim-bar-editing' : ''}`} style={dock} data-testid="client-mobile-bar"><div className="sim-mobile-price"><span>Total TTC</span><span className="sim-amount">{formatEuro(quote.total_ttc)}</span></div>{primary(true)}</div>}
  </div>;
}